import React, { useMemo } from "react";
import { GroundedResidualSystem } from "../../powerFlow/groundedProblem";
import { buildJacobian } from "../../powerFlow/nonlinear/jacobian";

interface Props {
  system?: GroundedResidualSystem;
  /** The current solution point, so entries can show their value there. */
  point?: ReadonlyMap<string, number>;
}

const CELL = 9;

/**
 * The Jacobian J[i][j] = ∂residualᵢ/∂xⱼ — the matrix Newton solves against each
 * iteration (J·Δ = −F). Shown as a sparsity pattern (one square per structurally
 * nonzero entry) over a table of the symbolic partials, each with its value at
 * the current solution.
 */
const JacobianView: React.FC<Props> = ({ system, point }) => {
  const jac = useMemo(() => (system ? buildJacobian(system, point) : null), [system, point]);

  if (!jac) {
    return (
      <div style={{ padding: "16px", color: "#666", fontStyle: "italic" }}>
        No residual system — run an AC power flow to see its Jacobian.
      </div>
    );
  }

  const n = jac.rows.length;
  const m = jac.cols.length;
  const nnz = jac.entries.length;
  const density = n && m ? (100 * nnz) / (n * m) : 0;

  const cellColor = (value?: number): string => {
    if (value === undefined) return "#64748b";
    if (value > 0) return "#2563eb";
    if (value < 0) return "#dc2626";
    return "#cbd5e1";
  };

  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ marginBottom: "8px" }}>
        <h4 style={{ margin: "0 0 4px 0", fontSize: "14px" }}>Jacobian ∂F/∂x</h4>
        <div style={{ fontSize: "12px", color: "#666" }}>
          {n} residuals × {m} variables · {nnz} nonzeros ({density.toFixed(1)}% dense)
          {point && (
            <>
              {" "}
              · <span style={{ color: "#2563eb" }}>▉</span> positive{" "}
              <span style={{ color: "#dc2626" }}>▉</span> negative at the solution
            </>
          )}
        </div>
      </div>

      {/* Sparsity pattern — rows are residuals (top→down), columns variables. */}
      <div style={{ overflow: "auto", maxHeight: "40%", border: "1px solid #eee", marginBottom: "12px" }}>
        <svg width={m * CELL + 1} height={n * CELL + 1} style={{ display: "block" }}>
          {jac.entries.map((e) => (
            <rect
              key={`${e.row}:${e.col}`}
              x={e.col * CELL + 0.5}
              y={e.row * CELL + 0.5}
              width={CELL - 1}
              height={CELL - 1}
              fill={cellColor(e.value)}
            >
              <title>{`∂${e.rowName}/∂${e.colName}${e.value !== undefined ? ` = ${e.value.toFixed(4)}` : ""}`}</title>
            </rect>
          ))}
        </svg>
      </div>

      {/* Symbolic entries. */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "12px" }}>
          <thead>
            <tr style={{ position: "sticky", top: 0, background: "#f8f8f8" }}>
              <th style={th}>∂ residual / ∂ variable</th>
              <th style={th}>symbolic</th>
              <th style={{ ...th, textAlign: "right" }}>at solution</th>
            </tr>
          </thead>
          <tbody>
            {jac.entries.map((e) => (
              <tr key={`${e.row}:${e.col}`}>
                <td style={mono}>
                  ∂{e.rowName} / ∂{e.colName}
                </td>
                <td style={mono}>{e.symbolic}</td>
                <td style={{ ...mono, textAlign: "right" }}>
                  {e.value !== undefined ? e.value.toFixed(4) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "4px 8px",
  borderBottom: "1px solid #ddd",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const mono: React.CSSProperties = {
  fontFamily: "monospace",
  padding: "3px 8px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "top",
};

export default JacobianView;
